/**
 * Outil « Circuit électrique » (Boîte à outils, physique-chimie 6e → 1re) — logique PURE.
 *
 * Quatre montages : une boucle, série (2 dipôles), dérivation (2 branches), mixte (D1 en série
 * avec D2 ∥ D3, à partir de la 2nde). Chaque dipôle est une lampe (6 V – 0,5 A, modélisée par
 * 12 Ω) ou un conducteur ohmique ; il peut être en place, retiré (lampe dévissée : branche
 * ouverte) ou court-circuité (un fil à ses bornes). Le générateur est idéal (tension E), ou réel
 * en 1re (E, r : U = E − rI). Un fil entre les bornes du générateur, ou une charge nulle : le
 * générateur est en court-circuit (intensité très grande, danger).
 *
 * `solveCircuit` donne l'intensité dans chaque branche, la tension aux bornes de chaque dipôle et
 * sa puissance ; `lawLines` écrit les lois vérifiées ; `makeQuiz` fabrique les questions du défi
 * selon le niveau (qualitatif en 6e / 5e, lois et loi d'Ohm en 4e, puissance et énergie en 3e,
 * lois des nœuds et des mailles en 2nde, source réelle et effet Joule en 1re).
 */

import {
  frNum,
  mcqOptions,
  pick,
  retryMcq,
  shuffle,
  sigNum,
  type Mcq,
  type McqOption,
  type Rng,
} from '../../ui/mcq';

export type { Rng };
export { shuffle };

export type CircuitId = 'loop' | 'series' | 'parallel' | 'mixed';
export type DipoleId = 'D1' | 'D2' | 'D3';
export type DipoleKind = 'lamp' | 'resistor';
export type DipoleState = 'ok' | 'removed' | 'shorted';
export type Glow = 'off' | 'dim' | 'normal' | 'bright';

export interface CircuitDef {
  id: CircuitId;
  label: string;
  dipoles: readonly DipoleId[];
  /** premier niveau où le montage est proposé (6e = 1 … 1re = 6) */
  minRank: number;
}

export const CIRCUITS: readonly CircuitDef[] = [
  { id: 'loop', label: 'Une boucle', dipoles: ['D1'], minRank: 1 },
  { id: 'series', label: 'Série', dipoles: ['D1', 'D2'], minRank: 1 },
  { id: 'parallel', label: 'Dérivation', dipoles: ['D1', 'D2'], minRank: 1 },
  { id: 'mixed', label: 'Mixte', dipoles: ['D1', 'D2', 'D3'], minRank: 5 },
];

export const circuitById = (id: CircuitId): CircuitDef => CIRCUITS.find((c) => c.id === id)!;

/** montages proposés à ce niveau (niveau inconnu : tous) */
export function circuitsFor(rank: number | null): CircuitDef[] {
  return CIRCUITS.filter((c) => rank === null || rank >= c.minRank);
}

/** Lampe 6 V – 0,5 A : 12 Ω, 3 W quand elle brille normalement. */
export const LAMP_R = 12;
export const LAMP_P_NOM = 3;
/** Valeurs de résistances proposées (série E12 usuelle des collèges), en ohms. */
export const RESISTORS = [10, 22, 33, 47, 68, 100, 150, 220, 330, 470, 680, 1000] as const;

export interface CircuitInput {
  id: CircuitId;
  kind: DipoleKind;
  /** tension du générateur à vide (V) */
  E: number;
  /** résistance interne (Ω) : 0 = générateur idéal */
  r: number;
  /** interrupteur fermé */
  closed: boolean;
  /** un fil relie directement les bornes du générateur */
  shortGen: boolean;
  /** résistance de chaque conducteur ohmique (les lampes valent `LAMP_R`) */
  R: Record<DipoleId, number>;
  state: Record<DipoleId, DipoleState>;
}

export interface DipoleReading {
  id: DipoleId;
  /** le dipôle fait partie du montage */
  inCircuit: boolean;
  state: DipoleState;
  /** résistance du dipôle (Ω) */
  R: number;
  /** tension à ses bornes (V) */
  U: number;
  /** intensité qui le traverse (A) — un dipôle court-circuité n'est traversé par rien : le fil prend tout */
  I: number;
  /** puissance reçue (W) */
  P: number;
  /** intensité dans le fil qui le court-circuite (A), 0 sinon */
  Ishort: number;
}

export interface CircuitResult {
  /** open : aucun courant ; on : le circuit fonctionne ; short : générateur en court-circuit */
  status: 'open' | 'on' | 'short';
  /** intensité débitée par le générateur (A) — Infinity en court-circuit d'un générateur idéal */
  I: number;
  /** tension aux bornes du générateur (V) */
  UG: number;
  /** puissance fournie par le générateur E·I et dissipée dans sa résistance interne r·I² (W) */
  PE: number;
  PJ: number;
  /** tension commune aux branches en dérivation (montage dérivation ou partie ∥ du mixte) */
  Upar: number;
  dipoles: Record<DipoleId, DipoleReading>;
}

const ALL: readonly DipoleId[] = ['D1', 'D2', 'D3'];

/** résistance effective : retiré = branche ouverte (∞), court-circuité = fil (0) */
function eff(input: CircuitInput, d: DipoleId): number {
  const s = input.state[d];
  if (s === 'removed') return Infinity;
  if (s === 'shorted') return 0;
  return dipoleR(input, d);
}

export function dipoleR(input: Pick<CircuitInput, 'kind' | 'R'>, d: DipoleId): number {
  return input.kind === 'lamp' ? LAMP_R : input.R[d];
}

function series(...rs: number[]): number {
  return rs.reduce((s, x) => s + x, 0);
}

function parallel(...rs: number[]): number {
  if (rs.some((x) => x === 0)) return 0;
  const g = rs.reduce((s, x) => s + (Number.isFinite(x) ? 1 / x : 0), 0);
  return g === 0 ? Infinity : 1 / g;
}

/** résistance de la charge vue par le générateur */
export function loadResistance(input: CircuitInput): number {
  const e = (d: DipoleId) => eff(input, d);
  switch (input.id) {
    case 'loop':
      return e('D1');
    case 'series':
      return series(e('D1'), e('D2'));
    case 'parallel':
      return parallel(e('D1'), e('D2'));
    case 'mixed':
      return series(e('D1'), parallel(e('D2'), e('D3')));
  }
}

export function solveCircuit(input: CircuitInput): CircuitResult {
  const used = new Set(circuitById(input.id).dipoles);
  const blank = (d: DipoleId): DipoleReading => ({
    id: d,
    inCircuit: used.has(d),
    state: input.state[d],
    R: dipoleR(input, d),
    U: 0,
    I: 0,
    P: 0,
    Ishort: 0,
  });
  const dipoles = Object.fromEntries(ALL.map((d) => [d, blank(d)])) as Record<
    DipoleId,
    DipoleReading
  >;
  const base = { I: 0, UG: input.E, PE: 0, PJ: 0, Upar: 0, dipoles };
  if (!input.closed) return { status: 'open', ...base };
  const Rload = input.shortGen ? 0 : loadResistance(input);
  if (Rload === 0) {
    const I = input.r > 0 ? input.E / input.r : Infinity;
    return {
      status: 'short',
      ...base,
      I,
      UG: 0,
      PE: input.E * I,
      PJ: input.r > 0 ? input.r * I * I : Infinity,
    };
  }
  if (!Number.isFinite(Rload)) return { status: 'open', ...base };

  const I = input.E / (Rload + input.r);
  const UG = input.E - input.r * I;
  const set = (d: DipoleId, i: number) => {
    // i : intensité dans la portion du circuit où se trouve le dipôle
    const s = input.state[d];
    if (s === 'removed') return;
    if (s === 'shorted') {
      dipoles[d] = { ...dipoles[d], Ishort: i };
      return;
    }
    const R = dipoleR(input, d);
    dipoles[d] = { ...dipoles[d], I: i, U: R * i, P: R * i * i };
  };
  let Upar = 0;
  switch (input.id) {
    case 'loop':
      set('D1', I);
      break;
    case 'series':
      set('D1', I);
      set('D2', I);
      break;
    case 'parallel': {
      Upar = UG;
      for (const d of ['D1', 'D2'] as const) {
        if (input.state[d] === 'ok') set(d, UG / dipoleR(input, d));
      }
      break;
    }
    case 'mixed': {
      set('D1', I);
      const Rp = parallel(eff(input, 'D2'), eff(input, 'D3'));
      Upar = Rp * I;
      for (const d of ['D2', 'D3'] as const) {
        const s = input.state[d];
        if (s === 'ok') set(d, Upar / dipoleR(input, d));
        // un dipôle de la partie ∥ court-circuité court-circuite toute la partie ∥
        if (s === 'shorted') set(d, I);
      }
      break;
    }
  }
  return {
    status: 'on',
    I,
    UG,
    PE: input.E * I,
    PJ: input.r * I * I,
    Upar,
    dipoles,
  };
}

/** éclat d'une lampe d'après sa puissance (3 W : éclat normal) */
export function glowOf(P: number): Glow {
  const x = P / LAMP_P_NOM;
  if (x < 0.03) return 'off';
  if (x < 0.4) return 'dim';
  if (x <= 1.3) return 'normal';
  return 'bright';
}

export const GLOW_TEXT: Record<Glow, string> = {
  off: 'éteinte',
  dim: 'brille faiblement',
  normal: 'brille normalement',
  bright: 'brille trop fort (elle risque de griller)',
};

/* ---------- écriture des nombres ---------- */

const NB = String.fromCharCode(0xa0);

/** nombre à la française, `d` décimales au plus (zéros inutiles retirés), « 18 000 » */
export const fr = frNum;

/** 3 chiffres significatifs, virgule */
export const sig = sigNum;

/** « 0,25 A », « 27,3 mA », « très grande » */
export function amps(I: number): string {
  if (!Number.isFinite(I)) return 'très grande';
  if (I !== 0 && Math.abs(I) < 1) return `${sig(I * 1000)}${NB}mA`;
  return `${sig(I)}${NB}A`;
}

export function volts(U: number): string {
  return `${sig(U)}${NB}V`;
}

export function watts(P: number): string {
  if (!Number.isFinite(P)) return 'très grande';
  if (P !== 0 && Math.abs(P) < 1) return `${sig(P * 1000)}${NB}mW`;
  return `${sig(P)}${NB}W`;
}

export function ohms(R: number): string {
  return R >= 1000 ? `${sig(R / 1000)}${NB}kΩ` : `${sig(R)}${NB}Ω`;
}

/** nom affiché d'un dipôle : L₁ pour une lampe, R₁ pour un conducteur ohmique */
export function dipoleName(kind: DipoleKind, d: DipoleId): string {
  return `${kind === 'lamp' ? 'L' : 'R'}${'₁₂₃'[Number(d[1]) - 1]}`;
}

const sub = (d: DipoleId) => '₁₂₃'[Number(d[1]) - 1];

/* ---------- lois vérifiées ---------- */

/**
 * Phrases du prof sous le schéma : ce qui se passe et la loi vérifiée, selon le niveau
 * (rang 6e = 1 … 1re = 6 ; null = tout).
 */
export function lawLines(input: CircuitInput, res: CircuitResult, rank: number | null): string[] {
  const k = input.kind;
  const name = (d: DipoleId) => dipoleName(k, d);
  const numbers = rank === null || rank >= 3;
  const out: string[] = [];
  const def = circuitById(input.id);
  if (!input.closed) {
    return ['Interrupteur ouvert : le circuit est ouvert, aucun courant ne circule.'];
  }
  if (res.status === 'short') {
    out.push(
      'Court-circuit du générateur ! Le courant passe par le fil sans rien traverser : l’intensité devient très grande, le générateur et les fils chauffent (risque d’incendie). À ne jamais faire.',
    );
    if (input.r > 0 && numbers)
      out.push(
        `Avec la résistance interne : I = E / r = ${fr(input.E, 2)} / ${fr(input.r, 2)} = ${amps(res.I)}.`,
      );
    return out;
  }
  if (res.status === 'open') {
    out.push(
      input.id === 'series' || input.id === 'loop'
        ? 'Un dipôle est retiré : la boucle est coupée, plus aucun courant ne circule.'
        : 'Le circuit est ouvert : plus aucun courant ne circule.',
    );
    return out;
  }
  const D = res.dipoles;
  const shorted = def.dipoles.filter((d) => input.state[d] === 'shorted');
  const removed = def.dipoles.filter((d) => input.state[d] === 'removed');
  for (const d of shorted)
    out.push(
      `${name(d)} est court-circuité${k === 'lamp' ? 'e' : ''} : le courant passe par le fil à ses bornes, ${
        k === 'lamp' ? 'elle s’éteint' : 'il ne reçoit plus rien'
      }.`,
    );
  if (input.id === 'parallel' || input.id === 'mixed') {
    for (const d of removed)
      if (d !== 'D1' || input.id === 'parallel')
        out.push(
          `${name(d)} est retiré${k === 'lamp' ? 'e' : ''} : sa branche est ouverte, les autres branches fonctionnent toujours.`,
        );
  }
  if (!numbers) {
    if (k === 'lamp') {
      const lit = def.dipoles
        .filter((d) => D[d].inCircuit && input.state[d] === 'ok')
        .map((d) => `${name(d)} ${GLOW_TEXT[glowOf(D[d].P)]}`);
      if (lit.length) out.push(`${lit.join(' ; ')}.`);
    }
    if (input.id === 'series')
      out.push(
        'En série, les dipôles forment une seule boucle : si l’un est retiré, tout s’arrête.',
      );
    if (input.id === 'parallel')
      out.push(
        'En dérivation, chaque lampe a sa propre boucle avec le générateur : elles fonctionnent indépendamment.',
      );
    return out;
  }
  const I = res.I;
  switch (input.id) {
    case 'loop':
      out.push(`Une seule boucle : la même intensité partout, I = ${amps(I)}.`);
      break;
    case 'series': {
      out.push(`Série : la même intensité traverse tout le circuit, I = ${amps(I)}.`);
      const parts = (['D1', 'D2'] as const).map((d) => volts(D[d].U));
      out.push(
        `Additivité des tensions : U = U${sub('D1')} + U${sub('D2')} → ${volts(res.UG)} = ${parts.join(' + ')}.`,
      );
      break;
    }
    case 'parallel': {
      out.push(`Dérivation : même tension aux bornes de chaque branche, ${volts(res.Upar)}.`);
      out.push(
        `Loi des nœuds : I = I${sub('D1')} + I${sub('D2')} → ${amps(I)} = ${amps(D.D1.I)} + ${amps(D.D2.I)}.`,
      );
      break;
    }
    case 'mixed': {
      const i2 = D.D2.I + D.D2.Ishort;
      const i3 = D.D3.I + D.D3.Ishort;
      out.push(
        `Loi des nœuds : I = I${sub('D2')} + I${sub('D3')} → ${amps(I)} = ${amps(i2)} + ${amps(i3)}.`,
      );
      out.push(
        `Loi des mailles : U = U${sub('D1')} + U${sub('D2')} → ${volts(res.UG)} = ${volts(D.D1.U)} + ${volts(res.Upar)}.`,
      );
      break;
    }
  }
  if (k === 'resistor') {
    const d = def.dipoles.find((x) => input.state[x] === 'ok' && D[x].I > 0);
    if (d)
      out.push(
        `Loi d’Ohm : U${sub(d)} = R${sub(d)} × I${sub(d)} = ${ohms(D[d].R)} × ${amps(D[d].I)} = ${volts(D[d].U)}.`,
      );
  } else {
    const off = def.dipoles.filter(
      (d) => input.state[d] === 'ok' && glowOf(D[d].P) !== 'normal' && D[d].inCircuit,
    );
    for (const d of off) out.push(`${name(d)} ${GLOW_TEXT[glowOf(D[d].P)]}.`);
  }
  if (rank === null || rank >= 4) {
    const total = def.dipoles.reduce((s, d) => s + D[d].P, 0);
    out.push(
      `Puissance : P = U × I ; le générateur fournit ${watts(res.UG * I)}, les dipôles reçoivent ${watts(total)}.`,
    );
  }
  if ((rank === null || rank >= 6) && input.r > 0) {
    out.push(
      `Source réelle : U = E − r × I = ${fr(input.E, 2)} − ${fr(input.r, 2)} × ${sig(I)} = ${volts(res.UG)}. Bilan : E × I = r × I² + U × I → ${watts(res.PE)} = ${watts(res.PJ)} (effet Joule) + ${watts(res.UG * I)}.`,
    );
  }
  return out;
}

/* ---------- défi ---------- */

export type QuizKind =
  | 'lamps-series'
  | 'lamps-parallel'
  | 'lamp-short'
  | 'gen-short'
  | 'series-current'
  | 'additivity'
  | 'node'
  | 'ohm'
  | 'ohm-r'
  | 'power'
  | 'energy'
  | 'mesh'
  | 'real-source'
  | 'joule';

export type QuizOption = McqOption;

export type Quiz = Mcq<QuizKind>;

/** cinq questions selon le niveau */
export function quizKinds(rank: number | null): QuizKind[] {
  if (rank !== null && rank <= 2)
    return ['lamps-series', 'lamps-parallel', 'lamp-short', 'gen-short', 'lamps-parallel'];
  if (rank === 3) return ['series-current', 'additivity', 'node', 'ohm', 'ohm-r'];
  if (rank === 4) return ['additivity', 'node', 'ohm', 'power', 'energy'];
  if (rank === 5) return ['node', 'mesh', 'ohm', 'ohm-r', 'power'];
  return ['node', 'mesh', 'ohm', 'real-source', 'joule'];
}

const options = (good: string, wrongs: Array<[string, string]>, rng: Rng): QuizOption[] =>
  mcqOptions(good, wrongs, rng);

/** une question du défi ; données retirées tant que deux choix s'écrivent pareil (4 choix) */
export function makeQuiz(kind: QuizKind, rng: Rng): Quiz {
  return retryMcq((r) => buildQuiz(kind, r), rng);
}

function buildQuiz(kind: QuizKind, rng: Rng): Quiz {
  const L = pick(['L₁', 'L₂'], rng);
  const other = L === 'L₁' ? 'L₂' : 'L₁';
  switch (kind) {
    case 'lamps-series':
      return {
        kind,
        question: `Deux lampes ${'L₁'} et ${'L₂'} sont branchées en série avec une pile. On dévisse ${L}. Que se passe-t-il ?`,
        options: options(
          'Les deux lampes s’éteignent',
          [
            [
              `Seule ${L} s’éteint, ${other} brille toujours`,
              'C’est vrai en dérivation, pas en série : en série il n’y a qu’une boucle.',
            ],
            [`${other} brille plus fort`, 'Plus aucun courant ne circule : la boucle est coupée.'],
            ['Rien ne change', 'Dévisser une lampe ouvre le circuit.'],
          ],
          rng,
        ),
        explain: `En série, ${'L₁'} et ${'L₂'} sont dans la même boucle : dévisser ${L} ouvre le circuit, le courant ne passe plus nulle part.`,
      };
    case 'lamps-parallel':
      return {
        kind,
        question: `Deux lampes ${'L₁'} et ${'L₂'} sont branchées en dérivation aux bornes d’une pile. On dévisse ${L}. Que devient ${other} ?`,
        options: options(
          `${other} brille toujours, comme avant`,
          [
            [
              `${other} s’éteint aussi`,
              'C’est le cas en série. En dérivation, chaque lampe a sa propre boucle avec la pile.',
            ],
            [
              `${other} brille deux fois plus`,
              'La tension aux bornes de chaque branche reste celle de la pile : rien ne change pour l’autre branche.',
            ],
            [
              `${other} brille faiblement`,
              'Sa branche n’a pas changé : elle reçoit toujours la tension de la pile.',
            ],
          ],
          rng,
        ),
        explain: `En dérivation, la branche de ${other} forme une boucle avec la pile, qui ne passe pas par ${L} : ${other} fonctionne indépendamment.`,
      };
    case 'lamp-short':
      return {
        kind,
        question: `Deux lampes identiques sont en série avec une pile et brillent faiblement. On relie les deux bornes de ${L} par un fil. Que se passe-t-il ?`,
        options: options(
          `${L} s’éteint et ${other} brille plus fort`,
          [
            [
              'Les deux lampes s’éteignent',
              'Le circuit reste fermé : le courant passe par le fil, puis par l’autre lampe.',
            ],
            [
              `${L} brille plus fort`,
              'Le courant prend le fil (aucune résistance) et ne traverse plus la lampe court-circuitée.',
            ],
            ['Rien ne change', 'Le fil court-circuite la lampe : le courant ne la traverse plus.'],
          ],
          rng,
        ),
        explain: `Le fil court-circuite ${L} : le courant passe par le fil, ${L} s’éteint. ${other} est maintenant seule avec la pile : elle reçoit toute la tension et brille plus fort.`,
      };
    case 'gen-short':
      return {
        kind,
        question:
          'On relie directement les deux bornes d’une pile par un fil de connexion. Que se passe-t-il ?',
        options: options(
          'Un court-circuit : le courant devient très grand, la pile chauffe',
          [
            [
              'Rien : il n’y a pas de lampe, donc pas de courant',
              'Le fil ferme le circuit : un courant très intense circule.',
            ],
            [
              'La pile se recharge',
              'Un fil ne fournit pas d’énergie : il laisse passer un courant très intense.',
            ],
            [
              'Le courant est faible car le fil est court',
              'Justement : sans dipôle pour le limiter, le courant devient très grand.',
            ],
          ],
          rng,
        ),
        explain:
          'Le fil relie les bornes sans rien traverser : c’est un court-circuit. L’intensité devient très grande, la pile et les fils chauffent (risque d’incendie). À ne jamais faire.',
      };
    case 'series-current': {
      const I = pick([120, 150, 200, 250, 300], rng);
      return {
        kind,
        question: `Dans un circuit en série (une pile, une lampe et un conducteur ohmique), l’ampèremètre placé avant la lampe indique ${I}${NB}mA. Quelle intensité indique-t-il placé après le conducteur ohmique ?`,
        options: options(
          `${I}${NB}mA`,
          [
            [
              `${fr(I / 2, 1)}${NB}mA`,
              'L’intensité ne se partage pas en série : il n’y a pas de nœud.',
            ],
            [
              `${2 * I}${NB}mA`,
              'L’intensité ne s’additionne pas en série : c’est la même partout.',
            ],
            ['0 mA', 'Le courant traverse tout le circuit : il ne « s’use » pas.'],
          ],
          rng,
        ),
        explain: `Loi d’unicité de l’intensité : en série, la même intensité traverse tous les dipôles, ${I}${NB}mA partout.`,
      };
    }
    case 'additivity': {
      const U = pick([4.5, 6, 9, 12], rng);
      const U1 = pick([1.5, 2, 2.5, 3], rng);
      const U2 = U - U1;
      return {
        kind,
        question: `Une lampe et un moteur sont en série avec un générateur de ${volts(U)}. La tension aux bornes de la lampe vaut ${volts(U1)}. Quelle est la tension aux bornes du moteur ?`,
        options: options(
          volts(U2),
          [
            [
              volts(U + U1),
              'On soustrait : la tension du générateur se partage entre les dipôles en série.',
            ],
            [
              volts(U),
              'En série, chaque dipôle ne reçoit qu’une partie de la tension du générateur.',
            ],
            [volts(U1), 'Les deux dipôles ne sont pas identiques : rien n’impose la même tension.'],
          ],
          rng,
        ),
        explain: `Additivité des tensions en série : U = U_lampe + U_moteur, donc U_moteur = ${volts(U)} − ${volts(U1)} = ${volts(U2)}.`,
      };
    }
    case 'node': {
      const I = pick([0.5, 0.6, 0.8, 1, 1.2], rng);
      const I1 = pick([0.2, 0.25, 0.3], rng);
      const I2 = I - I1;
      return {
        kind,
        question: `Un générateur alimente deux lampes en dérivation. Il débite ${amps(I)} ; la branche de la lampe ${'L₁'} est parcourue par ${amps(I1)}. Quelle est l’intensité dans la branche de ${'L₂'} ?`,
        options: options(
          amps(I2),
          [
            [
              amps(I + I1),
              'On soustrait : l’intensité de la branche principale se partage entre les branches.',
            ],
            [amps(I), 'Chaque branche ne reçoit qu’une partie du courant débité.'],
            [
              amps(I1),
              'Les branches n’ont aucune raison d’avoir la même intensité si les lampes diffèrent.',
            ],
          ],
          rng,
        ),
        explain: `Loi des nœuds : I = I₁ + I₂, donc I₂ = ${amps(I)} − ${amps(I1)} = ${amps(I2)}.`,
      };
    }
    case 'ohm': {
      const R = pick([47, 100, 150, 220, 330], rng);
      const ImA = pick([10, 20, 25, 30, 40], rng);
      const U = (R * ImA) / 1000;
      return {
        kind,
        question: `Un conducteur ohmique de résistance ${ohms(R)} est traversé par un courant de ${ImA}${NB}mA. Quelle est la tension à ses bornes ?`,
        options: options(
          volts(U),
          [
            [volts(R * ImA), 'Convertis d’abord l’intensité en ampères : 1 mA = 0,001 A.'],
            [volts(R / ImA), 'La loi d’Ohm est U = R × I, pas R ÷ I.'],
            [volts(ImA / 1000 / R), 'Il faut multiplier R par I, pas diviser I par R.'],
          ],
          rng,
        ),
        explain: `Loi d’Ohm : U = R × I = ${R} × ${fr(ImA / 1000, 3)} = ${volts(U)} (I en ampères).`,
      };
    }
    case 'ohm-r': {
      const U = pick([3, 4.5, 6, 9], rng);
      const ImA = pick([15, 20, 30, 45, 60], rng);
      const R = U / (ImA / 1000);
      return {
        kind,
        question: `Aux bornes d’un conducteur ohmique, on mesure ${volts(U)} quand il est traversé par ${ImA}${NB}mA. Quelle est sa résistance ?`,
        options: options(
          ohms(R),
          [
            [ohms(U / ImA), 'L’intensité doit être en ampères : 1 mA = 0,001 A.'],
            [ohms((U * ImA) / 1000), 'R = U ÷ I, pas U × I.'],
            [ohms(ImA / 1000 / U), 'R = U ÷ I : c’est la tension qu’on divise par l’intensité.'],
          ],
          rng,
        ),
        explain: `Loi d’Ohm : R = U ÷ I = ${fr(U, 2)} ÷ ${fr(ImA / 1000, 3)} = ${ohms(R)}.`,
      };
    }
    case 'power': {
      const U = pick([6, 12, 230], rng);
      const I = U === 230 ? pick([2, 5, 8], rng) : pick([0.5, 1.5, 2], rng);
      const P = U * I;
      return {
        kind,
        question: `Un appareil fonctionne sous ${volts(U)} et est parcouru par ${amps(I)}. Quelle puissance électrique reçoit-il ?`,
        options: options(
          watts(P),
          [
            [watts(U / I), 'P = U × I : on multiplie la tension par l’intensité.'],
            [watts(U + I), 'On multiplie, on n’additionne pas une tension et une intensité.'],
            [watts(I / U), 'P = U × I, pas I ÷ U.'],
          ],
          rng,
        ),
        explain: `P = U × I = ${fr(U, 2)} × ${fr(I, 2)} = ${watts(P)}.`,
      };
    }
    case 'energy': {
      const P = pick([60, 100, 1000, 2000], rng);
      const h = pick([2, 3, 5], rng);
      const kWh = (P / 1000) * h;
      return {
        kind,
        question: `Un appareil de ${P}${NB}W fonctionne pendant ${h}${NB}h. Quelle énergie électrique consomme-t-il ?`,
        options: options(
          `${fr(kWh, 2)}${NB}kWh`,
          [
            [`${fr(P * h, 0)}${NB}kWh`, 'Convertis la puissance en kilowatts : 1 000 W = 1 kW.'],
            [
              `${fr(P / h / 1000, 3)}${NB}kWh`,
              'E = P × t : on multiplie par la durée, on ne divise pas.',
            ],
            [
              `${fr(P * h * 3600, 0)}${NB}kWh`,
              'Avec P en kW et t en heures, E est directement en kWh.',
            ],
          ],
          rng,
        ),
        explain: `E = P × t = ${fr(P / 1000, 3)} kW × ${h} h = ${fr(kWh, 2)} kWh.`,
      };
    }
    case 'mesh': {
      const U = pick([9, 12], rng);
      const U1 = pick([3, 4.5, 5], rng);
      const Up = U - U1;
      return {
        kind,
        question: `Un générateur (${volts(U)}) alimente un conducteur ohmique R₁ en série avec deux lampes en dérivation. La tension aux bornes de R₁ vaut ${volts(U1)}. Quelle est la tension aux bornes de chaque lampe ?`,
        options: options(
          volts(Up),
          [
            [
              volts(Up / 2),
              'Les lampes en dérivation ont la MÊME tension : elle ne se partage pas entre elles.',
            ],
            [volts(U), 'La maille passe aussi par R₁ : U = U₁ + U_lampe.'],
            [volts(U + U1), 'On soustrait la tension de R₁ : loi des mailles U = U₁ + U_lampe.'],
          ],
          rng,
        ),
        explain: `Loi des mailles : U = U₁ + U_lampe, donc U_lampe = ${volts(U)} − ${volts(U1)} = ${volts(Up)}, la même pour les deux lampes en dérivation.`,
      };
    }
    case 'real-source': {
      const E = pick([4.5, 6, 9, 12], rng);
      const r = pick([0.5, 1, 1.5, 2], rng);
      const I = pick([0.4, 0.5, 0.8, 1], rng);
      const U = E - r * I;
      return {
        kind,
        question: `Une pile de force électromotrice E = ${volts(E)} et de résistance interne r = ${ohms(r)} débite ${amps(I)}. Quelle est la tension à ses bornes ?`,
        options: options(
          volts(U),
          [
            [volts(E + r * I), 'La résistance interne fait PERDRE de la tension : U = E − r × I.'],
            [volts(E), 'C’est la tension à vide. En débitant, la pile perd r × I.'],
            [volts(r * I), 'r × I est la chute de tension interne, pas la tension aux bornes.'],
          ],
          rng,
        ),
        explain: `Modèle de la source réelle : U = E − r × I = ${fr(E, 2)} − ${fr(r, 2)} × ${fr(I, 2)} = ${volts(U)}.`,
      };
    }
    case 'joule': {
      const R = pick([5, 10, 20, 50], rng);
      const I = pick([0.5, 2, 3], rng);
      const P = R * I * I;
      return {
        kind,
        question: `Un conducteur ohmique de ${ohms(R)} est parcouru par ${amps(I)}. Quelle puissance dissipe-t-il par effet Joule ?`,
        options: options(
          watts(P),
          [
            [watts(R * I), 'R × I est une tension (U), pas une puissance : P = R × I².'],
            [watts(R * R * I), 'C’est l’intensité qui est au carré : P = R × I².'],
            [watts(R / (I * I)), 'P = R × I², on multiplie par I².'],
          ],
          rng,
        ),
        explain: `Effet Joule : P = R × I² = ${fr(R, 2)} × ${fr(I, 2)}² = ${watts(P)}.`,
      };
    }
  }
}
